import type { RegisteredComponent } from "@builder.io/sdk-react";

import { useAuth } from "~/hec/context/auth-context";

type NameFormat = "first" | "last" | "full";

interface UserNameProps {
  format?: NameFormat;
  attributes?: Record<string, unknown>;
}

/**
 * UserName — renders the current user's name from auth context.
 * Displays first name, last name, or full name depending on the `format` input.
 * Returns null when the user is not logged in.
 */
const UserName = ({ format = "first", attributes }: UserNameProps) => {
  const { isLoggedIn, user } = useAuth();

  if (!isLoggedIn || !user) return null;

  let displayName = "";
  switch (format) {
    case "first":
      displayName = user.firstName ?? "";
      break;
    case "last":
      displayName = user.lastName ?? "";
      break;
    case "full":
      displayName = [user.firstName, user.lastName].filter(Boolean).join(" ");
      break;
  }

  if (!displayName) return null;

  return (
    <span
      {...(attributes as React.HTMLAttributes<HTMLSpanElement>)}
      data-testid="user-name"
    >
      {displayName}
    </span>
  );
};

export const UserNameDefinition = {
  component: UserName,
  name: "UserName",
  noWrap: true,
  canHaveChildren: false,
  image: "https://unpkg.com/css.gg@2.0.0/icons/svg/nametag.svg",
  inputs: [
    {
      name: "format",
      friendlyName: "Name Format",
      type: "string",
      enum: ["first", "last", "full"],
      defaultValue: "first",
      helperText: "Which parts of the user name to display",
    },
  ],
} satisfies RegisteredComponent;

export default UserName;
